// the unsafe assignments are caused by the lack of strict types on headers
/* eslint-disable  @typescript-eslint/no-unsafe-assignment */

import React, { PropsWithChildren, useMemo } from 'react';
import {
    ApolloClient,
    createHttpLink,
    InMemoryCache,
    ApolloProvider,
    split,
} from '@apollo/client';

import { setContext } from '@apollo/client/link/context';
import useAuth from 'Modules/firebase/use-auth';
import { QUALITY_USERLESS_CLIENT } from './constants';
import errorLink from './links/error-link';
import { appRoutes } from 'app/routes/constants';
import { useOperator } from "app/contexts/OperatorContext";

const ApolloWrapper: React.FC<PropsWithChildren> = ({ children }) => {
    const { getAccessTokenSilently, logout, isAuthenticated, user } = useAuth();
    const { organizationId } = useOperator();

    const client = useMemo(() => {
        const qualityHttpLink = createHttpLink({
            uri: process.env.GRAPHQL_SERVER_URL,
        });

        // auth link for auth base requests
        const authzLink = setContext(async (_, { headers }) => {
            const accessToken = isAuthenticated
                ? await getAccessTokenSilently()
                : '';

            // if we do not have a token, force logout
            if (!accessToken) {
                return null;
            }

            return {
                headers: {
                    authorization: accessToken ? `Bearer ${accessToken}` : '',
                    'x-org-id': organizationId,
                    ...headers,
                },
            };
        });

        const userlessLink = setContext((_, { headers }) => {
            return {
                headers: {
                    ...headers,
                    Authorization: `Bearer ${process.env.APPLICATION_USERLESS_TOKEN}`,
                },
            };
        });

        const mainHttpLink = split(
            (operation) => {
                return (
                    operation.getContext().clientName ===
                    QUALITY_USERLESS_CLIENT
                );
            },
            userlessLink.concat(qualityHttpLink),
            authzLink.concat(qualityHttpLink),
        );

        return new ApolloClient({
            name: 'quality-webapp',
            link: errorLink.concat(mainHttpLink),
            cache: new InMemoryCache(),
        });
    }, [user, organizationId]);

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloWrapper;
